import { useState, useEffect, useRef } from "react";
import { useLocalStorage } from "../../../contexts/LocalStorageContext";
import "../../../assets/css/mediaMixAnalysis.scss";
import {
  Box,
  Button,
  Chip,
  Collapse,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  LinearProgress,
  linearProgressClasses,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DateRangePicker, {
  DateRangeData,
} from "../../../shared/components/dateRangePicker";
import { QuickPresetsSelect } from "../../../shared/components/quickPresetsSelect";
import { fetchPeerSets } from "../../../shared/utils/fetchPeerSets";
import BenchmarkErrorPage from "../benchMarkError";
import { BenchMarkErrorType, BenchmarkScreenType } from "../../../shared/enum";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import PrintContainer from "../../../shared/components/printContainer";
import { ReportExportActions } from "../../../shared/components/reportExportButton";

// ─── Types ──────────────────────────────────────────────────────────────────────

interface DashboardData {
  mixedScore: { value: number; label: string; status: string };
  assessment: string;
  mixShift: {
    title: string;
    months: string[];
    series: { label: string; color: string; data: number[] }[];
  };
  spendAllocation: {
    totalSpend: string;
    channels: { label: string; pct: number; peer: number; color: string }[];
  };
  channels: Channel[];
}

interface Channel {
  name: string;
  vsPeers: string;
  peerStatus: string;
  summary: string;
  expanded: boolean;
  metrics:
    | {
        label: string;
        value: string;
        sub: string;
        trend: "up" | "down" | "neutral";
        valueStartPrefix?: string;
        valueEndPrefix?: string;
      }[]
    | null;
  tactical:
    | { label: string; budgetNote: string; roas: string; cpa: string }[]
    | null;
  alert: string | null;
}

// ─── Tokens ────────────────────────────────────────────────────────────────────

const DARK_BG = "#1e2d24";

const statusColor: Record<string, string> = {
  green: "#4a7c59",
  yellow: "#b5860d",
  red: "#c0392b",
};

// ─── Styled ────────────────────────────────────────────────────────────────────

const DarkPaper = styled(Paper)({
  backgroundColor: DARK_BG,
  color: "#fff",
  borderRadius: 16,
  padding: "24px 28px",
});

const ScoreProgress = styled(LinearProgress)({
  height: 5,
  borderRadius: 4,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: "rgba(255,255,255,0.15)",
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 4,
    backgroundColor: "#fff",
  },
});

const MetricBox = styled(Box)({});

// ─── Donut Chart (SVG, animated + hover tooltip) ──────────────────────────────

const DonutChart = ({
  channels,
  totalLabel,
}: {
  channels: DashboardData["spendAllocation"]["channels"];
  totalLabel: string;
}) => {
  const [hovered, setHovered] = useState<number | null>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(t);
  }, []);

  const cx = 220,
    cy = 190,
    r = 120,
    stroke = 40;
  const circumference = 2 * Math.PI * r;
  let offset = 0;

  const segments = channels.map((ch, i) => {
    const dash = (ch.pct / 100) * circumference;
    const seg = { ...ch, dash, offset, gap: circumference - dash, index: i };
    offset += dash;
    return seg;
  });

  const hoveredSeg = hovered !== null ? segments[hovered] : null;

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        aspectRatio: "1 / 1",
        mx: "auto",
        maxWidth: 300,
      }}
    >
      <svg
        viewBox="0 0 380 380"
        width={"100%"}
        // height={"100%"}
        style={{ transform: "rotate(-90deg)", display: "block" }}
      >
        <style>{`
          @keyframes donut-draw {
            from { stroke-dashoffset: 0; opacity: 0; }
            to   { opacity: 1; }
          }
        `}</style>
        {segments.map((seg) => {
          const isHovered = hovered === seg.index;
          return (
            <circle
              key={seg.label}
              cx={cx}
              cy={cy}
              r={r}
              fill="none"
              stroke={seg.color}
              strokeWidth={isHovered ? stroke + 8 : stroke}
              strokeDasharray={`${mounted ? seg.dash : 0} ${circumference}`}
              strokeDashoffset={-seg.offset}
              style={{
                transition:
                  "stroke-width 0.18s ease, stroke-dasharray 0.9s cubic-bezier(.4,0,.2,1)",
                cursor: "pointer",
                filter: isHovered
                  ? `drop-shadow(0 0 6px ${seg.color}88)`
                  : "none",
                opacity: hovered !== null && !isHovered ? 0.55 : 1,
              }}
              onMouseEnter={() => setHovered(seg.index)}
              onMouseLeave={() => setHovered(null)}
            />
          );
        })}
      </svg>

      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%,-90%)",
          textAlign: "center",
          pointerEvents: "none",
          transition: "all 0.2s ease",
          minWidth: 90,
        }}
      >
        {hoveredSeg ? (
          <>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                bgcolor: hoveredSeg.color,
                mx: "auto",
                mb: 0.5,
              }}
            />
            <Typography
              fontWeight={800}
              fontSize={26}
              color={DARK_BG}
              lineHeight={1}
            >
              {hoveredSeg.pct}%
            </Typography>
            <Typography
              variant="caption"
              fontWeight={600}
              color="text.secondary"
              display="block"
              mt={0.3}
            >
              {hoveredSeg.label}
            </Typography>
            <Typography variant="caption" color="text.disabled" display="block">
              Peer: {hoveredSeg.peer}%
            </Typography>
          </>
        ) : (
          <>
            <Typography variant="caption" className="donut-label">
              Total Spend
            </Typography>
            <Typography className="donut-value">{totalLabel}</Typography>
          </>
        )}
      </Box>
    </Box>
  );
};

// ─── Channel Row ───────────────────────────────────────────────────────────────

const ChannelRow = ({ ch }: { ch: Channel }) => {
  const [open, setOpen] = useState(ch.expanded);

  return (
    <Box>
      <Box sx={{ py: 1.5 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Box sx={{ flex: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1.5} mb={0.4}>
              <Typography className="channel-name">{ch.name}</Typography>
              <Typography className="peer-status">
                {ch.vsPeers} vs. Peers
              </Typography>
              <Box
                className="status-dot"
                sx={{ bgcolor: statusColor[ch.peerStatus] }}
              />
            </Stack>
            <Divider className="channel-divider" />
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography className="summary-text">{ch.summary}</Typography>
              <Button
                size="small"
                onClick={() => setOpen((p) => !p)}
                className="expand-btn"
              >
                {open ? "Close −" : "Expand +"}
              </Button>
            </Stack>
          </Box>
        </Stack>

        <Collapse in={open}>
          <Box sx={{ mt: 1.5 }}>
            {ch.metrics && (
              <Stack
                direction="row"
                spacing={1.5}
                mb={1.5}
                flexWrap="wrap"
                useFlexGap
                justifyContent="space-between"
              >
                {ch.metrics.map((m) => {
                  const isCostMetric = m.label === "Cost/Conv";

                  // Mathematical direction
                  const isDown = m.trend === "down";
                  const isUp = m.trend === "up";

                  // Cost/Conv is an inverted metric:
                  // lower = better, higher = worse
                  const isFavorable = isCostMetric ? isDown : isUp;
                  return (
                    <MetricBox key={m.label} className="metric-box">
                      <Typography className="metric-label">
                        {m.label}
                      </Typography>
                      <Typography className="metric-value">
                        <span className="value-start-prefix">
                          {m.valueStartPrefix ?? ""}
                        </span>
                        {m.value}
                        <span className="value-end-prefix">
                          {m.valueEndPrefix ?? ""}
                        </span>
                      </Typography>
                      <Divider className="metric-divider" />
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={0.3}
                        mt={0.5}
                      >
                        {isUp && (
                          <svg
                            className={
                              isFavorable
                                ? "trend-up-icon favorable"
                                : "trend-up-icon unfavorable"
                            }
                            xmlns="http://www.w3.org/2000/svg"
                            width="14"
                            height="15"
                            viewBox="0 0 14 15"
                            fill="none"
                          >
                            <path
                              d="M7.27072 0.294969C6.84736 -0.098323 6.15983 -0.098323 5.73648 0.294969L0.317517 5.32911C-0.105839 5.7224 -0.105839 6.3611 0.317517 6.7544C0.740873 7.14769 1.4284 7.14769 1.85176 6.7544L5.4215 3.43501V13.088C5.4215 13.6449 5.90582 14.0948 6.50529 14.0948C7.10476 14.0948 7.58908 13.6449 7.58908 13.088V3.43501L11.1588 6.75125C11.5822 7.14454 12.2697 7.14454 12.6931 6.75125C13.1164 6.35796 13.1164 5.71925 12.6931 5.32596L7.27411 0.291823L7.27072 0.294969Z"
                              fill={isFavorable ? "#528A4D" : "#BA4143"}
                            />
                          </svg>
                        )}
                        {isDown && (
                          <svg
                            className={
                              isFavorable
                                ? "trend-down-icon favorable"
                                : "trend-down-icon unfavorable"
                            }
                            xmlns="http://www.w3.org/2000/svg"
                            width="14"
                            height="15"
                            viewBox="0 0 14 15"
                            fill="none"
                          >
                            <path
                              d="M5.73978 13.7999C6.16313 14.1932 6.85066 14.1932 7.27402 13.7999L12.693 8.76574C13.1163 8.37245 13.1163 7.73374 12.693 7.34045C12.2696 6.94716 11.5821 6.94716 11.1587 7.34045L7.589 10.6598L7.589 1.00688C7.589 0.449975 7.10468 4.81211e-05 6.50521 4.80687e-05C5.90573 4.80163e-05 5.42142 0.449974 5.42142 1.00688L5.42141 10.6598L1.85167 7.3436C1.42832 6.9503 0.740787 6.9503 0.317431 7.3436C-0.105925 7.73689 -0.105925 8.37559 0.317431 8.76889L5.73639 13.803L5.73978 13.7999Z"
                              fill={isFavorable ? "#528A4D" : "#BA4143"}
                            />
                          </svg>
                        )}
                        <Typography
                          className={
                            ch.name === "Video" &&
                            m.trend === "neutral" &&
                            m.label !== "Spend %"
                              ? "trend-sub-neutral"
                              : isCostMetric
                                ? m.trend === "down"
                                  ? "trend-sub-up"
                                  : "trend-sub-down"
                                : m.trend === "down"
                                  ? "trend-sub-down"
                                  : "trend-sub-up"
                          }
                        >
                          {m.sub}
                        </Typography>
                      </Stack>
                    </MetricBox>
                  );
                })}
              </Stack>
            )}

            {(ch.tactical?.length ?? 0) > 0 && (
              <Box sx={{ mb: 1.5 }}>
                <Typography className="tactical-heading">
                  Tactical Breakdown
                </Typography>

                {ch.tactical?.map((t) => (
                  <Stack
                    key={t.label}
                    direction="row"
                    alignItems="center"
                    sx={{
                      mb: 0.75,
                      width: "100%",
                    }}
                  >
                    {/* Label */}
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ flex: 1 }}
                    >
                      {t.label === "Retargeting" ? (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="9"
                          height="9"
                          viewBox="0 0 9 9"
                          fill="none"
                        >
                          <circle cx="4.5" cy="4.5" r="4.5" fill="#BA4143" />
                        </svg>
                      ) : (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="9"
                          height="9"
                          viewBox="0 0 9 9"
                          fill="none"
                        >
                          <circle cx="4.5" cy="4.5" r="4.5" fill="#528A4D" />
                        </svg>
                      )}

                      <Typography className="tactical-label">
                        {t.label}
                      </Typography>
                    </Stack>

                    {/* Right-side values */}
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={2}
                      sx={{
                        justifyContent: "flex-end",
                        minWidth: "330px",
                      }}
                    >
                      <Typography
                        className="tactical-note"
                        sx={{ minWidth: "120px", textAlign: "right" }}
                      >
                        {t.budgetNote}
                      </Typography>

                      <Typography
                        className="tactical-value"
                        sx={{ minWidth: "70px", textAlign: "right" }}
                      >
                        ROAS: {t.roas}
                      </Typography>

                      <Typography
                        className="tactical-value"
                        sx={{ minWidth: "90px", textAlign: "right" }}
                      >
                        CPA: {t.cpa}
                      </Typography>
                    </Stack>
                  </Stack>
                ))}
              </Box>
            )}

            {ch.alert && (
              <Box className="alert-box">
                <InfoOutlinedIcon className="alert-icon" />
                <Typography className="alert-text">{ch.alert}</Typography>
              </Box>
            )}
          </Box>
        </Collapse>
      </Box>
    </Box>
  );
};

// ─── Main ──────────────────────────────────────────────────────────────────────

const MediaMixAnalysisReport = () => {
  const reportRef = useRef<HTMLDivElement>(null);
  const [trendPeriod, setTrendPeriod] = useState<"6months" | "1year" | "ytd">(
    "6months",
  );
  const [dateRange, setDateRange] = useState<DateRangeData>(() => {
    const today = new Date();

    const pastDate = new Date();
    pastDate.setDate(today.getDate() - 29);

    return {
      from: {
        year: pastDate.getFullYear(),
        month: pastDate.getMonth() + 1,
        day: pastDate.getDate(),
      },
      to: {
        year: today.getFullYear(),
        month: today.getMonth() + 1,
        day: today.getDate(),
      },
    };
  });
  const [loading, setLoading] = useState(false);
  const [peerSets, setPeerSets] = useState<
    { id: string; title: string; subtitles: string[] }[]
  >([]);
  const [selectedPeerSet, setSelectedPeerSet] = useState<{
    id: string;
    title: string;
  } | null>(null);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(
    null,
  );
  const [cohortPropertyCount, setCohortPropertyCount] = useState(0);
  const [errorType, setErrorType] = useState<BenchMarkErrorType>(
    BenchMarkErrorType.INVALID_FILTER,
  );

  const LAYER_BASE_URL = process.env.REACT_APP_API_LAYER_URL;
  const { propertyId, propertyTitle } = useLocalStorage();

  const handleDateChange = (v: DateRangeData) => setDateRange(v);
  const handleResetFilter = () => {};

  useEffect(() => {
    const loadPeerSets = async () => {
      if (!propertyId) return;
      setLoading(true);
      const { peerSets: fetched, defaultPeerSet } =
        await fetchPeerSets(propertyId);
      setPeerSets(fetched);
      if (defaultPeerSet) {
        setSelectedPeerSet({
          id: defaultPeerSet.id,
          title: defaultPeerSet.title,
        });
      }
    };
    loadPeerSets();
  }, [propertyId]);

  const formatDate = (d: { year: number; month: number; day: number }) =>
    `${d.year}-${String(d.month).padStart(2, "0")}-${String(d.day).padStart(2, "0")}`;

  const fetchDashboardData = async () => {
    if (
      !propertyId ||
      !dateRange?.from ||
      !dateRange?.to ||
      !selectedPeerSet?.id
    )
      return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        property_id: String(propertyId),
        startDate: formatDate(dateRange.from),
        endDate: formatDate(dateRange.to),
        period: trendPeriod,
      });
      if (selectedPeerSet?.id) {
        params.append("peerset_id", selectedPeerSet.id);
      }

      const response = await fetch(
        `${LAYER_BASE_URL}/benchmarking-reports/media-mix-analysis?${params.toString()}`,
      );

      const data = await response.json();

      if (!response.ok || data?.error) {
        const count = data?.error?.details?.cohort_property_count ?? 0;
        const code = data?.error?.code;
        setCohortPropertyCount(count);
        setErrorType(
          code === "INSUFFICIENT_COHORT"
            ? BenchMarkErrorType.NOT_ENOUGH_PEER
            : BenchMarkErrorType.INVALID_FILTER,
        );
        setDashboardData(null);
        return;
      }

      setCohortPropertyCount(0);
      setErrorType(BenchMarkErrorType.INVALID_FILTER);
      setDashboardData(data?.data);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [propertyId, selectedPeerSet, dateRange, trendPeriod]);

  const { mixedScore, assessment, spendAllocation, mixShift, channels } =
    dashboardData ?? {
      mixedScore: { value: 0, label: "", status: "" },
      assessment: "",
      mixShift: { title: "", months: [], series: [] },
      spendAllocation: { totalSpend: "", channels: [] },
      channels: [],
    };
  const xLabels = mixShift.months;

  const generateMMACSV = () => {
    // Headers based on MMA-DS-007 requirements
    const headers = [
      "Channel / Dimension",
      "Allocation %",
      "Peer Allocation %",
      "Metric",
      "Value",
      "Trend/Status",
      "Summary / Assessment",
    ];

    const rows: any[][] = [];

    // 1. Global Score & Assessment
    rows.push([
      "OVERALL MIX",
      "",
      "",
      "Mix Score",
      mixedScore?.value,
      mixedScore?.label,
      assessment,
    ]);
    rows.push([]);

    // 2. Channel Data with Sub-dimensions
    channels?.forEach((ch) => {
      // Get allocation data for this channel
      const alloc = spendAllocation?.channels?.find((s) => s.label === ch.name);

      // Parent Channel Row
      rows.push([
        ch.name,
        alloc ? `${alloc.pct}%` : "",
        alloc ? `${alloc.peer}%` : "",
        "Performance Index",
        ch.vsPeers,
        ch.peerStatus,
        ch.summary,
      ]);

      // Nested Metrics for the Parent (Revenue, ROAS, etc.)
      ch.metrics?.forEach((m) => {
        rows.push([
          "", // Indent channel
          "",
          "",
          m.label,
          `${m.valueStartPrefix ?? ""}${m.value}${m.valueEndPrefix ?? ""}`,
          m.sub,
          "",
        ]);
      });

      // Sub-dimension rows (Prospecting / Retargeting)
      // Requirement: Must NOT contain peer benchmark values
      ch.tactical?.forEach((t) => {
        rows.push([
          `   > ${t.label}`, // Indented sub-dimension
          t.budgetNote, // Specific spend note
          "N/A", // Requirement: no peer benchmark for sub-dims
          "ROAS / CPA",
          `${t.roas} / ${t.cpa}`,
          "",
          "",
        ]);
      });

      // Alerts
      if (ch.alert) {
        rows.push(["", "", "", "ALERT", ch.alert, "", ""]);
      }

      rows.push([]); // Space between channels
    });

    return { headers, rows };
  };

  const { headers: csvHeaders, rows: csvRows } = generateMMACSV();

  return (
    <Box
      sx={{ bgcolor: "transparent", minHeight: "100vh", px: 5, pt: 1 }}
      ref={reportRef}
      className="benchmarks-report-root"
    >
      <PrintContainer
        title="Media Mix Analysis Report"
        propertyTitle={propertyTitle}
      >
        <Container maxWidth={false} disableGutters>
          {/* Header */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={2.5}
          >
            <Typography
              variant="h5"
              fontWeight={700}
              letterSpacing="-0.5px"
              className="report-heading"
            >
              Media Mix Analysis
            </Typography>
          </Stack>

          {/* Filters + Export */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={3}
            className="no-print"
          >
            <Stack direction="row" gap="12px" alignItems="center">
              <Box className="control-group">
                <span style={{ marginTop: "1px" }} className="date-range-label">
                  Date Range:
                </span>
                <DateRangePicker
                  dateRange={dateRange}
                  onChange={handleDateChange}
                  setDateRange={setDateRange}
                  setFilterData={handleResetFilter}
                  inputClassName="dateRangePickerInput"
                />
              </Box>
              <Box className="control-group">
                <span className="date-range-label">Peer Set:</span>
                <QuickPresetsSelect
                  presets={peerSets}
                  selectedId={selectedPeerSet?.id}
                  onSelect={(preset) => setSelectedPeerSet(preset)}
                  placeholder={selectedPeerSet?.title}
                />
              </Box>
            </Stack>
            <ReportExportActions
              filename="Media_Mix_Analysis"
              csvHeaders={csvHeaders}
              csvData={csvRows}
              componentRef={reportRef}
              loading={loading}
            />
          </Stack>

          {loading && (
            <Paper className="card-panel" elevation={0}>
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="70vh"
              >
                <CircularProgress />
              </Box>
            </Paper>
          )}
          {!dashboardData && !loading && (
            <BenchmarkErrorPage
              currentPeerCount={cohortPropertyCount}
              errorType={errorType}
              screenType={BenchmarkScreenType.MMA}
            />
          )}
          {dashboardData && !loading && (
            <Box className="report-content-wrapper">
              <>
                {/* Top Row: Score + Assessment */}
                <Grid container spacing={2} mb={2}>
                  <Grid item xs={12} md={2.5}>
                    <DarkPaper className="score-card">
                      <Typography variant="caption" className="report-label">
                        MIX SCORE
                      </Typography>
                      <Typography className="score-value">
                        {mixedScore.value}
                      </Typography>
                      <Chip
                        label={mixedScore.label}
                        size="small"
                        className="score-chip"
                      />
                    </DarkPaper>
                  </Grid>

                  <Grid item xs={12} md={9.5}>
                    <DarkPaper className="assessment-card">
                      <Typography
                        variant="caption"
                        className="assessment-label"
                      >
                        Strategic Media Mix Assessment
                      </Typography>
                      <Typography variant="body2" className="assessment-text">
                        {assessment}
                      </Typography>
                    </DarkPaper>
                  </Grid>
                </Grid>

                {/* Main body: Spend Allocation + Channel Insights */}
                <Grid container spacing={2} mb={2}>
                  {/* Left column */}
                  <Grid item xs={12} md={5.5}>
                    <Stack spacing={2}>
                      {/* Spend Allocation */}
                      <Paper className="spend-allocation-paper">
                        <Typography className="card-heading">
                          Spend Allocation
                        </Typography>
                        {spendAllocation?.channels?.length ? (
                          <>
                            <DonutChart
                              channels={spendAllocation.channels}
                              totalLabel={spendAllocation.totalSpend}
                            />
                            <Stack spacing={1} mt={-5}>
                              {spendAllocation.channels.map((ch) => (
                                <Stack
                                  key={ch.label}
                                  direction="row"
                                  className="channel-row"
                                >
                                  <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={1}
                                  >
                                    <Box
                                      sx={{
                                        borderRadius: "50%",
                                        bgcolor: ch.color,
                                      }}
                                      className="channel-dot"
                                    />
                                    <Typography
                                      variant="body2"
                                      className="channel-label"
                                    >
                                      {ch.label}
                                    </Typography>
                                  </Stack>
                                  <Typography variant="body2">
                                    <Box
                                      component="span"
                                      className="channel-value"
                                    >
                                      {ch.pct}%
                                    </Box>
                                    <span className="channel-peer">
                                      {"  "}Peer: {ch.peer}%
                                    </span>
                                  </Typography>
                                </Stack>
                              ))}
                            </Stack>
                          </>
                        ) : (
                          <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            py={6}
                          >
                            <Typography variant="body2" color="textSecondary">
                              No data available
                            </Typography>
                          </Box>
                        )}
                      </Paper>

                      {/* How your mix is shifting */}
                      <Paper
                        elevation={0}
                        sx={{
                          borderRadius: "10px",
                          background: "#FFF",
                          boxShadow: "0 4px 14.4px 0 rgba(0, 0, 0, 0.10)",
                          p: "24px 20px 16px",
                        }}
                      >
                        <Typography
                          className="mix-shift-title"
                          sx={{ pl: "34px" }}
                        >
                          {mixShift.title}
                        </Typography>
                        {mixShift?.series?.length ? (
                          <>
                            <Stack
                              direction="row"
                              className="mix-legend"
                              useFlexGap
                              sx={{
                                pl: "34px",
                                flexWrap: "wrap",
                                gap: 2,
                              }}
                            >
                              {mixShift.series.map((s) => (
                                <Stack
                                  key={s.label}
                                  direction="row"
                                  alignItems="center"
                                  spacing={0.5}
                                >
                                  <Box
                                    sx={{
                                      width: "15px",
                                      height: "15px",
                                      borderRadius: "50%",
                                      bgcolor: s.color,
                                    }}
                                  />
                                  <span className="legend-label">
                                    {s.label}
                                  </span>
                                </Stack>
                              ))}
                            </Stack>

                            <ResponsiveContainer
                              width="100%"
                              height={320}
                              className="mix-shift-chart"
                            >
                              <BarChart
                                data={xLabels.map((month, i) => ({
                                  month,
                                  ...Object.fromEntries(
                                    mixShift.series.map((s) => [
                                      s.label,
                                      s.data[i],
                                    ]),
                                  ),
                                }))}
                                margin={{
                                  top: 5,
                                  right: 10,
                                  left: -10,
                                  bottom: 20,
                                }}
                                barGap={0}
                                barCategoryGap="12%"
                              >
                                <XAxis
                                  dataKey="month"
                                  axisLine={false}
                                  tickLine={false}
                                  interval={0}
                                  textAnchor="end"
                                  height={30}
                                />
                                <YAxis
                                  axisLine={false}
                                  tickLine={false}
                                  tickFormatter={(v) => `${v}%`}
                                  domain={[0, 100]}
                                  ticks={[0, 25, 50, 75, 100]}
                                />
                                <Tooltip
                                  cursor={false}
                                  contentStyle={{
                                    borderRadius: 8,
                                    border: "none",
                                    boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                                    fontSize: 12,
                                  }}
                                  formatter={(value: number, name: string) => [
                                    `${value}%`,
                                    name,
                                  ]}
                                />
                                {mixShift.series.map((s) => (
                                  <Bar
                                    key={s.label}
                                    dataKey={s.label}
                                    stackId="mix"
                                    fill={s.color}
                                    // radius={[2, 2, 0, 0]}
                                  />
                                ))}
                              </BarChart>
                            </ResponsiveContainer>
                          </>
                        ) : (
                          <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            py={6}
                          >
                            <Typography variant="body2" color="textSecondary">
                              No data available
                            </Typography>
                          </Box>
                        )}

                        {/* Period toggle */}
                        <Stack
                          direction="row"
                          justifyContent="center"
                          mt={0.5}
                          mb="46px"
                        >
                          <ToggleButtonGroup
                            value={trendPeriod}
                            exclusive
                            onChange={(_, v) => v && setTrendPeriod(v)}
                            size="small"
                            className="period-toggle"
                          >
                            <ToggleButton value="6months">
                              6 Months
                            </ToggleButton>
                            <ToggleButton value="1year">12 Months</ToggleButton>
                            <ToggleButton value="ytd">YTD</ToggleButton>
                          </ToggleButtonGroup>
                        </Stack>
                      </Paper>
                    </Stack>
                  </Grid>

                  {/* Right column: Channel Insights */}
                  <Grid item xs={12} md={6.5}>
                    <Paper className="channel-insights-paper">
                      <Typography className="channel-insights-heading" mb={0.5}>
                        Channel insights
                      </Typography>
                      {channels?.length ? (
                        channels.map((ch) => (
                          <ChannelRow key={ch.name} ch={ch} />
                        ))
                      ) : (
                        <Box
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          py={6}
                        >
                          <Typography variant="body2" color="textSecondary">
                            No data available
                          </Typography>
                        </Box>
                      )}
                    </Paper>
                  </Grid>
                </Grid>
              </>
            </Box>
          )}
        </Container>
      </PrintContainer>
    </Box>
  );
};

export default MediaMixAnalysisReport;
